import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
    Box,
    Typography,
    Paper,
    Container,
    Button,
    IconButton,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Alert,
} from "@mui/material";
import {
    ArrowBackIosNew as ArrowLeftIcon,
    ArrowForwardIos as ArrowRightIcon,
    Refresh as RefreshIcon,
    CollectionsBookmark as FlashCardsIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { fetchTriviaQuestions } from "../../utils/openTriviaApi";

const AMOUNT_OPTIONS = [5, 10, 15, 20];

/** Open Trivia category IDs mapped to our subject areas (Maths, English, Verbal, Non-Verbal Reasoning) */
const SUBJECT_TRIVIA_CATEGORIES = [
    { key: "maths", label: "Maths", categoryId: 19 },
    { key: "english", label: "English", categoryId: 10 },
    { key: "verbal-reasoning", label: "Verbal Reasoning", categoryId: 9 },
    { key: "non-verbal-reasoning", label: "Non-Verbal Reasoning", categoryId: 25 },
];

// To get enough 11+ style questions, we request more from the API
// and then filter down to the exact amount the user asked for.
const API_AMOUNT_MULTIPLIER = 4;

/** Simple keyword filters to roughly match UK 11+ style questions per subject. */
const SUBJECT_11PLUS_KEYWORDS = {
    maths: [
        "add",
        "subtract",
        "multiply",
        "divide",
        "+",
        "-",
        "×",
        "÷",
        "fraction",
        "percent",
        "%",
        "ratio",
        "angle",
        "perimeter",
        "area",
        "volume",
        "sequence",
        "mean",
        "median",
        "mode",
        "probability",
        "equation",
        "expression",
        "average",
        "round",
        "multiple",
        "factor",
        "square number",
    ],
    english: [
        "synonym",
        "antonym",
        "vowel",
        "consonant",
        "spelling",
        "prefix",
        "suffix",
        "meaning",
        "definition",
        "grammar",
        "punctuation",
        "verb",
        "noun",
        "adjective",
        "adverb",
        "sentence",
    ],
    "verbal-reasoning": [
        "alphabet",
        "code",
        "letter",
        "sequence",
        "pattern",
        "analogy",
        "similar",
        "opposite",
        "complete the pair",
        "hidden word",
    ],
    "non-verbal-reasoning": [
        "shape",
        "figure",
        "pattern",
        "rotation",
        "reflection",
        "symmetry",
        "mirror",
        "grid",
        "odd one out",
        "matrix",
        "net",
        "cube",
        "3d",
    ],
};

const HARD_EXCLUDE_KEYWORDS = [
    "calculus",
    "derivative",
    "integral",
    "matrix",
    "vector",
    "complex number",
    "quantum",
    "relativity",
    "theorem",
    "polynomial",
    "logarithm",
];

const is11PlusFriendlyQuestion = (q, subjectKey) => {
    // Only keep multiple choice, avoid boolean trivia
    if (q.type && q.type !== "multiple") return false;
    if (q.difficulty === "hard") return false;

    const text = `${q.question} ${q.correctAnswer} ${(q.incorrectAnswers || []).join(" ")}`.toLowerCase();
    if (HARD_EXCLUDE_KEYWORDS.some((bad) => text.includes(bad))) return false;

    const keywords = SUBJECT_11PLUS_KEYWORDS[subjectKey] || [];
    if (!keywords.length) return true;

    return keywords.some((kw) => text.includes(kw));
};

const QuizStyleFlashCards = () => {
    const navigate = useNavigate();
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [subjectKey, setSubjectKey] = useState("maths");
    const [amount, setAmount] = useState(10);
    const [difficulty, setDifficulty] = useState("");
    const [currentIndex, setCurrentIndex] = useState(0);
    const [flipped, setFlipped] = useState(false);
    const [hasFetched, setHasFetched] = useState(false);

    const selectedSubject = SUBJECT_TRIVIA_CATEGORIES.find((s) => s.key === subjectKey) || SUBJECT_TRIVIA_CATEGORIES[0];

    const loadQuestions = useCallback(async () => {
        setLoading(true);
        setError(null);

        // Try the requested difficulty first, then fall back to easier / any
        // so that each difficulty choice still shows questions for students.
        const difficultyOrderMap = {
            easy: ["easy", ""],
            medium: ["medium", "easy", ""],
            hard: ["hard", "medium", "easy", ""],
            "": [""],
        };
        const diffKey = difficulty || "";
        const difficultyChain = difficultyOrderMap[diffKey] || [diffKey];

        let finalList = [];

        for (const diffOption of difficultyChain) {
            const apiAmount = Math.min(50, amount * API_AMOUNT_MULTIPLIER);
            const { questions: qs, error: err } = await fetchTriviaQuestions({
                amount: apiAmount,
                category: selectedSubject.categoryId,
                difficulty: diffOption || undefined,
                type: "multiple",
            });

            // If the API itself fails, just stop and show no questions for now.
            if (err) {
                finalList = [];
                break;
            }

            const filtered = qs.filter((q) => is11PlusFriendlyQuestion(q, selectedSubject.key));
            finalList = filtered.slice(0, amount);

            // If we got enough suitable 11+ questions, stop trying other difficulties.
            if (finalList.length) {
                break;
            }
        }

        setLoading(false);
        setQuestions(finalList);
        setCurrentIndex(0);
        setFlipped(false);
        setHasFetched(true);
    }, [amount, selectedSubject, difficulty]);

    const currentQuestion = useMemo(
        () => (questions.length ? questions[currentIndex] : null),
        [questions, currentIndex]
    );

    const goCard = useCallback(
        (dir) => {
            setFlipped(false);
            setCurrentIndex((prev) => {
                const next = prev + dir;
                if (next < 0) return 0;
                if (next >= questions.length) return questions.length - 1;
                return next;
            });
        },
        [questions.length]
    );

    const handleKeyDown = useCallback(
        (e) => {
            if (!currentQuestion) return;
            if (e.key === "ArrowLeft") goCard(-1);
            if (e.key === "ArrowRight") goCard(1);
            if (e.key === " ") {
                e.preventDefault();
                setFlipped((f) => !f);
            }
        },
        [currentQuestion, goCard]
    );

    useEffect(() => {
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [handleKeyDown]);

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            <Box sx={{ mb: 3, display: "flex", alignItems: "center", justifyContent: "flex-start", flexWrap: "wrap", gap: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                        sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                            bgcolor: "#6366f1",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 4px 12px rgba(99, 102, 241, 0.35)",
                        }}
                    >
                        <FlashCardsIcon sx={{ fontSize: 28, color: "#fff" }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" fontWeight="bold" sx={{ color: "#1e293b", mb: 0 }}>
                            Quiz Style Flash Cards
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#374151" }}>
                            Maths, English, Verbal & Non-Verbal Reasoning from Open Trivia Database. Click the card to flip and reveal the answer.
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Controls */}
            <Paper
                elevation={0}
                sx={{
                    p: 2,
                    mb: 3,
                    borderRadius: 3,
                    border: "1px solid #e5e7eb",
                    bgcolor: "#f9fafb",
                }}
            >
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-end" }}>
                    <FormControl sx={{ minWidth: 140 }} size="small">
                        <InputLabel>Amount</InputLabel>
                        <Select value={amount} label="Amount" onChange={(e) => setAmount(e.target.value)}>
                            {AMOUNT_OPTIONS.map((n) => (
                                <MenuItem key={n} value={n}>{n}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl sx={{ minWidth: 200 }} size="small">
                        <InputLabel>Subject</InputLabel>
                        <Select value={subjectKey} label="Subject" onChange={(e) => setSubjectKey(e.target.value)}>
                            {SUBJECT_TRIVIA_CATEGORIES.map((s) => (
                                <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl sx={{ minWidth: 140 }} size="small">
                        <InputLabel>Difficulty</InputLabel>
                        <Select value={difficulty} label="Difficulty" onChange={(e) => setDifficulty(e.target.value)}>
                            <MenuItem value="">Any</MenuItem>
                            <MenuItem value="easy">Easy</MenuItem>
                            <MenuItem value="medium">Medium</MenuItem>
                            <MenuItem value="hard">Hard</MenuItem>
                        </Select>
                    </FormControl>
                    <Button
                        variant="contained"
                        onClick={loadQuestions}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                        sx={{
                            borderRadius: 999,
                            textTransform: "none",
                            fontWeight: 600,
                            px: 3,
                            bgcolor: "#6366f1",
                            "&:hover": { bgcolor: "#374151" },
                        }}
                    >
                        {loading ? "Loading..." : "Load Questions"}
                    </Button>
                </Box>
            </Paper>

            {/* Card area */}
            {hasFetched && !loading && (
                <>
                    {questions.length === 0 ? (
                        <Typography sx={{ color: "#374151", textAlign: "center", py: 4 }}>
                            No questions found. Try a different subject or difficulty.
                        </Typography>
                    ) : (
                        <Paper
                            elevation={0}
                            sx={{
                                p: 4,
                                borderRadius: 3,
                                border: "1px solid #e5e7eb",
                                bgcolor: "#ffffff",
                                minHeight: 480,
                                position: "relative",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    minHeight: 440,
                                    perspective: "1000px",
                                }}
                            >
                                <Box
                                    onClick={() => setFlipped((f) => !f)}
                                    sx={{
                                        width: "100%",
                                        maxWidth: 600,
                                        minHeight: 380,
                                        cursor: "pointer",
                                        position: "relative",
                                        transformStyle: "preserve-3d",
                                        transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
                                        transition: "transform 0.5s ease",
                                    }}
                                >
                                    {/* Front */}
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            width: "100%",
                                            height: "100%",
                                            backfaceVisibility: "hidden",
                                            WebkitBackfaceVisibility: "hidden",
                                            p: 4,
                                            pt: 3.5,
                                            borderRadius: 3,
                                            border: "3px solid #6366f1",
                                            bgcolor: "#eef2ff",
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "flex-start",
                                            overflowY: "auto",
                                        }}
                                    >
                                        <Chip
                                            label={currentQuestion?.difficulty}
                                            size="small"
                                            sx={{ mb: 2 }}
                                            color={currentQuestion?.difficulty === "easy" ? "success" : currentQuestion?.difficulty === "hard" ? "error" : "warning"}
                                        />
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                color: "#1e293b",
                                                fontWeight: 600,
                                                textAlign: "center",
                                                lineHeight: 1.6,
                                                overflowWrap: "break-word",
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            {currentQuestion?.question}
                                        </Typography>
                                        {currentQuestion?.options?.length > 0 && (
                                            <Box sx={{ mt: 2, width: "100%", textAlign: "left" }}>
                                                {currentQuestion.options.map((opt, i) => (
                                                    <Typography
                                                        key={opt}
                                                        variant="body1"
                                                        sx={{
                                                            color: "#374151",
                                                            py: 0.5,
                                                            px: 1.5,
                                                            borderRadius: 1,
                                                            bgcolor: "rgba(255,255,255,0.6)",
                                                            mb: 0.75,
                                                            "&:last-of-type": { mb: 0 },
                                                        }}
                                                    >
                                                        {String.fromCharCode(65 + i)}. {opt}
                                                    </Typography>
                                                ))}
                                            </Box>
                                        )}
                                        <Typography variant="body2" sx={{ mt: 2, color: "#374151" }}>
                                            Click to reveal correct answer
                                        </Typography>
                                    </Box>

                                    {/* Back */}
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            width: "100%",
                                            height: "100%",
                                            backfaceVisibility: "hidden",
                                            WebkitBackfaceVisibility: "hidden",
                                            transform: "rotateY(180deg)",
                                            p: 4,
                                            borderRadius: 3,
                                            border: "3px solid #10b981",
                                            bgcolor: "#ecfdf5",
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "flex-start",
                                            overflowY: "auto",
                                        }}
                                    >
                                        <Typography variant="caption" sx={{ color: "#059669", fontWeight: 700, mb: 1 }}>
                                            Answer
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                color: "#065f46",
                                                fontWeight: 700,
                                                textAlign: "center",
                                                lineHeight: 1.5,
                                            }}
                                        >
                                            {currentQuestion?.correctAnswer}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mt: 2, color: "#374151" }}>
                                            Click to show question
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Navigation */}
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    mt: 2,
                                    px: 1,
                                }}
                            >
                                <IconButton
                                    onClick={() => goCard(-1)}
                                    disabled={currentIndex <= 0}
                                    sx={{
                                        bgcolor: "rgba(99, 102, 241, 0.1)",
                                        "&:hover": { bgcolor: "rgba(99, 102, 241, 0.2)" },
                                        "&.Mui-disabled": { opacity: 0.5 },
                                    }}
                                >
                                    <ArrowLeftIcon />
                                </IconButton>
                                <Typography variant="body2" sx={{ color: "#374151", fontWeight: 600 }}>
                                    {currentIndex + 1} / {questions.length}
                                </Typography>
                                <IconButton
                                    onClick={() => goCard(1)}
                                    disabled={currentIndex >= questions.length - 1}
                                    sx={{
                                        bgcolor: "rgba(99, 102, 241, 0.1)",
                                        "&:hover": { bgcolor: "rgba(99, 102, 241, 0.2)" },
                                        "&.Mui-disabled": { opacity: 0.5 },
                                    }}
                                >
                                    <ArrowRightIcon />
                                </IconButton>
                            </Box>
                        </Paper>
                    )}
                </>
            )}

            {!hasFetched && !loading && !error && (
                <Paper
                    elevation={0}
                    sx={{
                        p: 6,
                        borderRadius: 3,
                        border: "1px dashed #e5e7eb",
                        bgcolor: "#fafafa",
                        textAlign: "center",
                    }}
                >
                    <Typography sx={{ color: "#374151", mb: 2 }}>
                        Choose options above and click <strong>Load Questions</strong> to start.
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={loadQuestions}
                        sx={{
                            borderRadius: 999,
                            textTransform: "none",
                            fontWeight: 600,
                            px: 4,
                            bgcolor: "#6366f1",
                            "&:hover": { bgcolor: "#374151" },
                        }}
                    >
                        Load Questions
                    </Button>
                </Paper>
            )}
        </Container>
    );
};

export default QuizStyleFlashCards;
